import React, { useState, useEffect } from 'react'
import { Lightbulb, Save, RotateCcw, Plus } from 'lucide-react'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Button,
 AlertMessage,
 InfoBox,
 SettingsPageSkeleton,
} from '../../components/ui'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { 
 StatusFormModal,
 SuggestionCard,
 DraggableStatusItem
} from '../../components/leads'
import { useSalesFunnels } from '../../hooks/useSalesFunnels'
import { KanbanStatus } from '../../types'
import { COLOR_OPTIONS, VEHICLE_STATUS_OPTIONS, FUNNEL_SUGGESTIONS } from '../../constants/funnelSuggestions'

const DEFAULT_STATUSES: KanbanStatus[] = [
 { 
 id: '1', 
 label: 'Novo', 
 value: 'new', 
 color: '#3B82F6',
 description: 'Leads que acabaram de entrar no sistema',
 locked: true,
 vehicleStatus: null,
 isFirst: true
 },
 { 
 id: '2', 
 label: 'Em Contato', 
 value: 'contacted', 
 color: '#EAB308',
 description: 'Leads que já foram contatados pela primeira vez',
 vehicleStatus: 'reserved'
 },
 { 
 id: '3', 
 label: 'Em Negociação', 
 value: 'negotiating', 
 color: '#8B5CF6',
 description: 'Leads em processo de negociação',
 vehicleStatus: 'reserved'
 },
 { 
 id: '4', 
 label: 'Fechado', 
 value: 'closed', 
 color: '#10B981',
 description: 'Leads que resultaram em venda',
 locked: true,
 vehicleStatus: 'sold',
 isClosed: true
 },
 { 
 id: '5', 
 label: 'Perdido', 
 value: 'lost', 
 color: '#EF4444',
 description: 'Leads que não converteram',
 locked: true,
 vehicleStatus: 'in_stock',
 isLost: true
 }
]

export const LeadsSettingsPage: React.FC = () => {
 const { defaultFunnel, isLoading: isFunnelLoading, updateFunnel, createFunnel, fetchDefaultFunnel } = useSalesFunnels()
 
 const [statuses, setStatuses] = useState<KanbanStatus[]>(DEFAULT_STATUSES)
 const [isCreating, setIsCreating] = useState(false)
 const [hasChanges, setHasChanges] = useState(false)
 const [isSaving, setIsSaving] = useState(false)
 const [saveSuccess, setSaveSuccess] = useState(false)
 const [saveError, setSaveError] = useState<string | null>(null)
 
 // Modals
 const [isFormModalOpen, setIsFormModalOpen] = useState(false)
 const [editingStatus, setEditingStatus] = useState<KanbanStatus | null>(null)
 
 // Confirm dialogs
 const [confirmReset, setConfirmReset] = useState(false)
 const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
 const [confirmApplySuggestion, setConfirmApplySuggestion] = useState<typeof FUNNEL_SUGGESTIONS[0] | null>(null)

 const sensors = useSensors(
 useSensor(PointerSensor),
 useSensor(KeyboardSensor, {
 coordinateGetter: sortableKeyboardCoordinates,
 })
 )

 useEffect(() => {
 if (defaultFunnel?.stages && defaultFunnel.stages.length > 0) {
 const mappedStatuses: KanbanStatus[] = defaultFunnel.stages
 .sort((a, b) => a.order - b.order)
 .map(stage => ({
 id: stage.id || `stage-${stage.order}`,
 label: stage.name,
 value: stage.value || stage.name.toLowerCase().replace(/\s+/g, '_'),
 color: stage.color,
 description: stage.description,
 locked: stage.isFirst || stage.isClosed || stage.isLost,
 vehicleStatus: stage.vehicleStatus,
 isFirst: stage.isFirst,
 isClosed: stage.isClosed,
 isLost: stage.isLost
 }))
 setStatuses(mappedStatuses)
 setIsCreating(false)
 setHasChanges(false)
 } else if (!isFunnelLoading && !defaultFunnel) {
 setStatuses(DEFAULT_STATUSES)
 setIsCreating(true)
 setHasChanges(true)
 }
 }, [defaultFunnel, isFunnelLoading])

 const handleDragEnd = (event: DragEndEvent) => {
 const { active, over } = event

 if (over && active.id !== over.id) {
 setStatuses((items) => {
 const oldIndex = items.findIndex((item) => item.id === active.id)
 const newIndex = items.findIndex((item) => item.id === over.id)

 if (newIndex === 0 && items[0].locked) return items
 if (items[oldIndex].locked) return items
 if (newIndex === items.length - 1 && items[items.length - 1].locked) return items

 return arrayMove(items, oldIndex, newIndex)
 })
 setHasChanges(true)
 setSaveSuccess(false)
 }
 }

 const handleSave = async () => {
 setIsSaving(true)
 setSaveError(null)
 
 try {
 if (isCreating || !defaultFunnel) {
 const stages = statuses.map((status, index) => ({
 name: status.label,
 description: status.description,
 color: status.color,
 order: index,
 vehicleStatus: status.vehicleStatus || undefined,
 isFirst: status.isFirst || false,
 isClosed: status.isClosed || false,
 isLost: status.isLost || false
 }))
 
 const newFunnel = await createFunnel({
 name: 'Funil de Vendas',
 description: 'Funil personalizado para gerenciamento de leads',
 isActive: true,
 isDefault: true,
 stages
 })
 
 if (newFunnel) {
 setIsCreating(false)
 }
 } else {
 const stages = statuses.map((status, index) => {
 const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(status.id)
 
 const stage: any = {
 name: status.label,
 description: status.description,
 color: status.color,
 order: index,
 vehicleStatus: status.vehicleStatus || undefined,
 isFirst: status.isFirst || false,
 isClosed: status.isClosed || false,
 isLost: status.isLost || false
 }
 
 if (isUUID) {
 stage.id = status.id
 }
 
 return stage
 })
 await updateFunnel(defaultFunnel.id, { stages })
 }
 
 setHasChanges(false)
 setSaveSuccess(true)
 setTimeout(() => setSaveSuccess(false), 3000)
 await fetchDefaultFunnel()
 } catch (error) {
 setSaveError('Erro ao salvar configurações. Tente novamente.')
 setTimeout(() => setSaveError(null), 5000)
 } finally {
 setIsSaving(false)
 }
 }

 const handleResetConfirm = () => {
 setStatuses(DEFAULT_STATUSES)
 setHasChanges(true)
 setSaveSuccess(false)
 setConfirmReset(false)
 }

 const handleEdit = (status: KanbanStatus) => {
 setEditingStatus(status)
 setIsFormModalOpen(true)
 }

 const handleSaveStatus = (updatedStatus: KanbanStatus) => {
 if (editingStatus) {
 // Edição
 setStatuses(prev => prev.map(s => {
 if (s.id === editingStatus.id) {
 return {
 ...updatedStatus,
 isFirst: s.isFirst,
 isClosed: s.isClosed,
 isLost: s.isLost,
 locked: s.isFirst || s.isClosed || s.isLost ? true : updatedStatus.locked
 }
 }
 return s
 }))
 } else {
 // Adição - inserir antes do último (Perdido)
 const newStatus: KanbanStatus = {
 ...updatedStatus,
 id: `${Date.now()}`
 }
 
 setStatuses(prev => {
 const allStatuses = [...prev]
 allStatuses.splice(allStatuses.length - 1, 0, newStatus)
 return allStatuses
 });

 }
 
 setHasChanges(true)
 setSaveSuccess(false)
 setEditingStatus(null)
 setIsFormModalOpen(false)
 }

 const handleDeleteConfirm = () => {
 if (confirmDelete) {
 setStatuses(prev => prev.filter(s => s.id !== confirmDelete))
 setHasChanges(true)
 setSaveSuccess(false)
 setConfirmDelete(null)
 }
 }

 const handleApplySuggestionConfirm = () => {
 if (confirmApplySuggestion) {
 const newStatuses: KanbanStatus[] = confirmApplySuggestion.statuses.map((s, idx) => ({
 id: `temp-${Date.now()}-${idx}`,
 label: s.label,
 value: s.value,
 color: s.color,
 description: s.description,
 locked: s.locked,
 vehicleStatus: s.vehicleStatus || null,
 isFirst: idx === 0,
 isClosed: s.locked && s.value === 'closed',
 isLost: s.locked && s.value === 'lost'
 }))
 setStatuses(newStatuses)
 setHasChanges(true)
 setSaveSuccess(false)
 setConfirmApplySuggestion(null)
 }
 }

 if (isFunnelLoading && !defaultFunnel) {
 return <SettingsPageSkeleton />
 }

 return (
 <div className="space-y-5">
 <Card className="border-border">
 <CardContent className="p-5 sm:p-6">
 <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
 <div className="space-y-1.5">
 <div className="flex items-center gap-2.5">
 <h1 className="text-xl font-semibold text-foreground sm:text-2xl">Configurações de Leads</h1>
 {isCreating && !saveSuccess && (
 <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
 Funil não criado
 </span>
 )}
 {!isCreating && !saveSuccess && (
 <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
 Ativo
 </span>
 )}
 </div>
 <p className="text-sm text-muted-foreground">
 Defina e ordene as etapas do funil de vendas para o Kanban de leads.
 </p>
 </div>
 <div className="flex items-center gap-2">
 <Button variant="outline" size="sm" onClick={() => setConfirmReset(true)} disabled={isSaving}>
 <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
 Restaurar padrão
 </Button>
 <Button size="sm" onClick={handleSave} disabled={!hasChanges || isSaving}>
 <Save className="h-3.5 w-3.5 mr-1.5" />
 {isSaving ? 'Salvando...' : isCreating ? 'Criar funil' : 'Salvar alterações'}
 </Button>
 </div>
 </div>
 </CardContent>
 </Card>

 {!isFunnelLoading && isCreating && !saveSuccess && (
 <AlertMessage
 variant="warning"
 message="Você ainda não configurou seu funil de vendas. Configure as etapas abaixo e clique em criar funil para começar."
 />
 )}
 {saveSuccess && (
 <AlertMessage
 variant="success"
 message={isCreating ? 'Funil criado com sucesso.' : 'Configurações salvas com sucesso.'}
 />
 )}
 {saveError && <AlertMessage variant="error" message={saveError} />}

 <Card className="border-border">
 <CardContent className="p-5 sm:p-6">
 <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
 Fluxo atual
 </p>
 <div className="flex items-center gap-1.5 flex-wrap">
 {statuses.map((s, i) => (
 <div key={s.id} className="flex items-center gap-1.5">
 <div
 className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium bg-card text-foreground"
 style={{ borderColor: `${s.color}66` }}
 >
 <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
 {s.label}
 </div>
 {i < statuses.length - 1 && <span className="text-muted-foreground/50 text-xs px-0.5">→</span>}
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 <InfoBox icon={Lightbulb} title="Como funciona" variant="info">
 Arraste as etapas para reorganizar. As etapas inicial, fechada e perdida são fixas e não podem ser removidas.
 Novas etapas são inseridas antes da etapa final.
 </InfoBox>

 <Card className="border-border">
 <CardHeader className="pb-3">
 <div className="flex items-center justify-between gap-3">
 <div>
 <CardTitle>Etapas do funil</CardTitle>
 <p className="text-sm text-muted-foreground mt-0.5">
 {statuses.length} etapa{statuses.length !== 1 ? 's' : ''} configurada{statuses.length !== 1 ? 's' : ''}
 </p>
 </div>
 <Button onClick={() => setIsFormModalOpen(true)} size="sm">
 <Plus className="h-3.5 w-3.5 mr-1.5" />
 Nova etapa
 </Button>
 </div>
 </CardHeader>
 <CardContent className="p-4 sm:p-6">
 <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
 <SortableContext items={statuses.map((s) => s.id)} strategy={verticalListSortingStrategy}>
 <div className="space-y-2">
 {statuses.map((status, index) => (
 <DraggableStatusItem
 key={status.id}
 status={status}
 index={index}
 onEdit={handleEdit}
 onDelete={(id) => setConfirmDelete(id)}
 vehicleStatusOptions={VEHICLE_STATUS_OPTIONS}
 />
 ))}
 </div>
 </SortableContext>
 </DndContext>
 </CardContent>
 </Card>

 <Card className="border-border">
 <CardHeader className="pb-3">
 <div className="flex items-center gap-2">
 <Lightbulb className="h-4 w-4 text-accent" />
 <div>
 <CardTitle>Modelos prontos</CardTitle>
 <p className="text-sm text-muted-foreground mt-0.5">
 Aplique um modelo pré-configurado de funil de vendas.
 </p>
 </div>
 </div>
 </CardHeader>
 <CardContent className="p-4 sm:p-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {FUNNEL_SUGGESTIONS.map((suggestion, idx) => (
 <SuggestionCard key={idx} suggestion={suggestion} onApply={(s) => setConfirmApplySuggestion(s)} />
 ))}
 </div>
 </CardContent>
 </Card>

 <StatusFormModal
 isOpen={isFormModalOpen}
 onClose={() => {
 setIsFormModalOpen(false)
 setEditingStatus(null)
 }}
 onSave={handleSaveStatus}
 status={editingStatus}
 colorOptions={COLOR_OPTIONS}
 vehicleStatusOptions={VEHICLE_STATUS_OPTIONS}
 />

 <ConfirmDialog
 isOpen={confirmReset}
 onClose={() => setConfirmReset(false)}
 onConfirm={handleResetConfirm}
 title="Restaurar Configuração Padrão?"
 description="Isso irá restaurar a ordem e configurações padrão das etapas. As etapas personalizadas serão removidas. Esta ação não pode ser desfeita até você salvar novamente."
 confirmText="Restaurar"
 cancelText="Cancelar"
 type="warning"
 />

 <ConfirmDialog
 isOpen={!!confirmDelete}
 onClose={() => setConfirmDelete(null)}
 onConfirm={handleDeleteConfirm}
 title="Remover Etapa?"
 description="Tem certeza que deseja remover esta etapa do kanban? Esta ação não pode ser desfeita. Os leads que estiverem nesta etapa precisarão ser movidos manualmente."
 confirmText="Remover"
 cancelText="Cancelar"
 type="danger"
 />

 <ConfirmDialog
 isOpen={!!confirmApplySuggestion}
 onClose={() => setConfirmApplySuggestion(null)}
 onConfirm={handleApplySuggestionConfirm}
 title={`Aplicar modelo "${confirmApplySuggestion?.title}"?`}
 description="Isso irá substituir sua configuração atual de etapas do kanban. Você poderá editar e reorganizar as etapas após aplicar o modelo."
 confirmText="Aplicar Modelo"
 cancelText="Cancelar"
 type="info"
 />
 </div>
 )
}
